import { FC, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";

import { AuthContext } from "src/context/authContext";
import { on_therapy, PatientFilterStateActions } from "src/pages/patient/types";
import { PATIENT_CURRENTLY_ON_TREATMENT_HEADER_LISTING, PATIENT_CURRENTLY_ON_TREATMENT_MANUFACTURE_HEADER_LISTING } from "src/constants/patients";
import { OnTherapyPatientProps } from "../types";
import { format, parse } from "date-fns";
import AddNote from "src/components/modals/addNote";
import ReRunPatientModal from "src/components/modals/rerunPatientModal";
import { handleNavigation, isNotKAMUser } from "src/helpers";
import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { ToastContext } from "src/context/toastContext";
import { LoaderContext } from "src/context/loaderContext";
import { ToastTypes } from "src/types";
import RequestDateOfTreatment from "src/components/modals/requestDateOfTreatment";
import CancelTreatment from "src/components/modals/cancelTreatment";
import { DEFAULT_REF_PAGE_SELECTION } from "src/constants";
import PracticeContactInfo from "src/components/practiceContactInfo";
import { ColumnSortProps } from "src/components/tableHeader/types";
import TableHeader from "src/components/tableHeader";
import { sortTableColumns } from "src/helpers/stats";



const OnTherapyPatient: FC<OnTherapyPatientProps> = ({ dataList, totalPages, currentPage, changePageNumber }): JSX.Element => {
  const { isManufacturer, defaultView, userData } = useContext(AuthContext);
  const location = useLocation();

  const [pageSearch, setPageSearch] = useState(currentPage.toString());
  const [pendingDataList, updatePendingDataList] = useState<on_therapy[]>([]);
  const [sortingConfig, setSortingConfig] = useState<ColumnSortProps>({
    sort_by: '',
    direction: 'desc',
  });

  const checkForPageChange = (page: { selected: number }): void => {
    changePageNumber({
      type: PatientFilterStateActions.CHANGE_ON_THERAPY_PAGE,
      payload: page.selected + 1,
    });
    setPageSearch((page.selected + 1).toString())

  };
  const { showToast } = useContext(ToastContext);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const [saveNoteModal, setDataforNoteModal] = useState<{ isModalVisible: boolean; caseId: number; }>({
    isModalVisible: false,
    caseId: 0,
  });
  const [cancelTreatmentModal, setDataForCancelModal] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId: number }>({
    isModalVisible: false,
    caseId: 0,
    prescriptionId: 0
  });
  const [showRequestTreatmentModal, setDataForRequestModalTreatment] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId: number, bentype: string ,investigated_at?: string}>({
    isModalVisible: false,
    caseId: 0,
    prescriptionId: 0,
    bentype: "",
    investigated_at: ""
  });
  const [showReRunPatientModal, setShowReRunPatientModal] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId: number, title: string, bentype: string; investigated_at?: string }>({
    isModalVisible: false,
    caseId: 0,
    prescriptionId: 0,
    title: '',
    bentype: ""
  });

  useEffect(() => {
    updatePendingDataList(dataList);
    setSortingConfig({ direction: 'desc', sort_by: '' })
  }, [dataList]);

  const { data: transferRxData, mutate: transferRxByCaseId } =
    useFetch(APIEndpoints.TRANSFER_RX);

  useEffect(() => {
    if (transferRxData) {
      hideLoader();
      const { message } = transferRxData as unknown as { code: number; message: string; };
      showToast([message], ToastTypes.SUCCESS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferRxData]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const ConfirmAlert = (title: string, buttonName: string, caseId: number) => {
    const result = window.confirm(title);
    if (result) {
      setLoader()
      transferRxByCaseId({}, { case_id: caseId }, { rep_xfer: true })
    }
  }

  const navigate = useNavigate();
  const navigateToModalAndPage = (item: on_therapy) => {
    if (item.re_run_type === 'popup') {
      setShowReRunPatientModal({ caseId: item.bi_case_id, isModalVisible: true, prescriptionId: item.prescription_id, title: "Please update case status below", bentype: "med", investigated_at: item.bi_completed ? parse(item.bi_completed, "MM/dd/yyyy", new Date()).toISOString() : undefined })
    } else {
      navigate(`/cases/${item ? item.bi_case_id : 0}/clone`)
    }
  }
  const handlePageSearch = () => {
    const pageNumber = Number(pageSearch);

    if (!pageNumber || pageNumber < 1 || pageNumber > totalPages) {
      return;
    }

    changePageNumber({
      type: PatientFilterStateActions.CHANGE_ON_THERAPY_PAGE,
      payload: pageNumber,
    });

    setPageSearch(pageNumber.toString());
  };

  const changeIdOrder = (value: string) => {
    sortTableColumns(value, sortingConfig, setSortingConfig, pendingDataList, updatePendingDataList)
  };

  const redirectDecision = () => {
    const isRedirectRequired = handleNavigation(defaultView, location.pathname);
    if (isRedirectRequired) {
      if (isNotKAMUser(userData?.ref_type ?? ""))
        navigate(defaultView.includes("/") ? defaultView : `/${defaultView}`, { replace: true })
      else {
        if (DEFAULT_REF_PAGE_SELECTION.find((dashboard) => dashboard.label.toLowerCase().includes("patient"))) {
          navigate(defaultView.includes("/") ? defaultView : `/${defaultView}`, { replace: true })
        } else {
          navigate(location.pathname.includes("patient") ? `${location.pathname}${location.search}` : "/patient_statistics", { replace: true })
        }
      }
    } else {
      navigate(0)
    }
  }

  return (
    <div className="card">
      {/* <div className="card-header custom-bg-purple">
        <h3 className="text-white mb-0">
          Patients Currently on Treatment
        </h3>
      </div> */}

      <div className="card-body p-0">
        <div className="custom-bg-light-purple p-2">
          {!isManufacturer ? <p className="mb-0 text-dark fs-6">
            These are patients currently on therapy. <i className="fas fa-comment"></i> indicates cases with new notes.
          </p> : <p className="mb-0 text-dark fs-6">
            These are patients currently on therapy.
          </p>}
        </div>

        <div className="table-responsive overflow-auto" style={{ maxHeight: "500px" }}>
          <table className="table table-striped table-sm mb-0">
            <thead className="sticky-top" style={{ zIndex: 0 }}>
              <tr className="text-center align-items-center">
                {isManufacturer ? (
                  PATIENT_CURRENTLY_ON_TREATMENT_MANUFACTURE_HEADER_LISTING
                    .filter((tableHeader) => tableHeader.value !== "reviewed_by_fam" || userData?.ref_type === "rep")
                    .map((tableHeader, index) => (
                    <TableHeader
                      key={index} tableHeader={tableHeader} sortConfig={sortingConfig} 
                      changeIdOrder={changeIdOrder}
                    />
                  ))
                ) : (
                  PATIENT_CURRENTLY_ON_TREATMENT_HEADER_LISTING.map((tableHeader, index) => (
                    <TableHeader 
                      key={index} tableHeader={tableHeader} sortConfig={sortingConfig} 
                      changeIdOrder={changeIdOrder}
                    />
                  ))
                )}
              </tr>
            </thead>

            <tbody>
              {pendingDataList.length > 0 ?
                pendingDataList.map((item, index) => (
                  <tr key={index} className="align-middle text-center">
                    {isManufacturer ?
                      (item.patient_name !== 'Redacted'
                        ?
                        (<td className="align-middle">
                          <Link
                            className="link-offset-0 link-offset-0-hover link-underline link-underline-opacity-0 link-underline-opacity-100-hover p-0 align-baseline"
                            to={`/global_patients/${item.patient_id}/edit`}
                          >
                            {item.patient_name}
                          </Link>

                        </td>)
                        :
                        (
                          <td className="align-middle">
                            {item.patient_name}
                          </td>
                        )) :
                      (<td className="align-middle">
                        <Link
                          className="link-offset-0 link-offset-0-hover link-underline link-underline-opacity-0 link-underline-opacity-100-hover p-0 align-baseline"
                          to={`/global_patients/${item.patient_id}/edit`}
                        >
                          {item.patient_name}
                        </Link>
                      </td>)
                    }
                    {/* <td className="align-middle">
                      <Link to={`/global_patients/${item.patient_id}/edit`}>
                        {item.patient_id}
                      </Link>
                    </td>
                    <td className="text-primary">
                      {isManufacturer ?
                        item.indication.toLowerCase() === 'other' ?
                          <p className="fw-bold text-danger mb-0">{item.bi_case_id}</p> :
                          <Link to={`/cases/${item.bi_case_id}`}>
                            {item.message_icon && !isManufacturer && <i className="fas fa-comment me-1"></i>}{item.bi_case_id}
                          </Link>
                        :
                        <Link to={`/cases/${item.bi_case_id}`}>
                          {item.message_icon && !isManufacturer && <i className="fas fa-comment me-1"></i>}{item.bi_case_id}
                        </Link>
                      }
                    </td> */}
                    {userData && isNotKAMUser(userData?.ref_type) ?
                      <td className="align-middle text-center">
                        <Link to={`/global_patients/${item.patient_id}/edit`}>
                          {item.patient_id}
                        </Link>
                      </td> :
                      <td className="align-middle text-center text-black">
                        {/* <Link to={`/global_patients/${item.patient_id}/edit`}> */}
                        {item.patient_id}
                        {/* </Link> */}
                      </td>
                    }
                    <td className="align-middle text-center">
                      {isManufacturer ? (
                        (userData && isNotKAMUser(userData?.ref_type))
                          ?
                          <>
                            {item.indication.toLowerCase() === "other" ? (
                              <p className="fw-bold text-danger mb-0">{item.bi_case_id}</p>
                            ) : (
                              <Link to={`/cases/${item.bi_case_id}`}>
                                {item.message_icon && !isManufacturer && <i className="fas fa-comment me-1"></i>}{item.bi_case_id}
                              </Link>
                            )}
                          </> :
                          <>
                            {item.indication.toLowerCase() === "other" ? (
                              <p className="fw-bold text-danger mb-0">{item.bi_case_id}</p>
                            ) : (
                              <p className="align-middle text-center text-black mb-0">
                                {item.message_icon && !isManufacturer && <i className="fas fa-comment me-1"></i>}{item.bi_case_id}
                              </p>
                            )}
                          </>
                      ) : (
                        <Link to={`/cases/${item.bi_case_id}`}>
                          {item.message_icon && !isManufacturer && <i className="fas fa-comment me-1"></i>}{item.bi_case_id}
                        </Link>
                      )}
                    </td>
                    {userData?.ref_type === "rep" &&
                      <td className="align-middle">
                        {item.reviewed_by_fam}
                      </td>
                    }
                    <td className="align-middle">
                      {isManufacturer ? item.payer : item.insurance_type}
                    </td>
                    <td className="align-middle">
                      <PracticeContactInfo
                        name={item.practice_name} 
                        address={item.practice_full_address} 
                        phone={item.practice_phone_number ?? ""} 
                        fax={item.practice_fax_number ?? ""}
                        indexKey={index}
                      >
                        <span>{item.provider}</span>
                      </PracticeContactInfo>
                    </td>
                    <td className="text-light align-middle background-color-3">
                      {item.treatment_status}
                    </td>
                    {/* {userData && !isNotKAMUser(userData.ref_type) ? <td className="text-light align-middle background-color-3">
                      {item.treatment_status}
                    </td> : (
                      <td className={`text-center text-light align-middle`} id="next-steps">
                        {item.indication.toLowerCase() === "other" ? <span className="text-black">No Action Available</span> : null}
                        {item && item.next_steps && item.indication.toLowerCase() !== "other" && item.next_steps.length > 0 &&
                          item.next_steps.map((step, index) => {
                            if (step === "Treated") {
                              return (
                                <button key={index} className="btn btn-green btn-sm m-1 rounded-0"
                                  onClick={() => setDataForRequestModalTreatment({ isModalVisible: true, caseId: item.bi_case_id, prescriptionId: item.prescription_id, bentype: "med", investigated_at: item.investigated_at ||"" })}
                                  type="button"
                                >
                                  <i className="fa-clipboard far"></i> Treated
                                </button>
                              );
                            } else if (step === "Not Treating") {
                              return (
                                <button key={index} className="btn btn-green btn-sm m-1 rounded-0"
                                  onClick={() => setDataForCancelModal({ isModalVisible: true, caseId: item.bi_case_id, prescriptionId: item.prescription_id })}
                                  type="button"
                                >
                                  <i className="fa-times fas"></i> Not Treated
                                </button>
                              );
                            } else if (step === "Not Treated") {
                              return (
                                <button key={index} className="btn btn-green btn-sm m-1 rounded-0"
                                  onClick={() => setDataForCancelModal({ isModalVisible: true, caseId: item.bi_case_id, prescriptionId: item.prescription_id })}
                                  type="button"
                                >
                                  <i className="fa-times fas"></i> Not Treated
                                </button>
                              );
                            } else if (step === "Transferred") {
                              return (
                                <button key={index} className="btn btn-green btn-sm m-1 rounded-0"
                                  onClick={() => ConfirmAlert('By clicking this you are noting the office has tranferred this prescription to a Specialty Pharmacy. Are you sure you want to mark this prescription transferred? This cannot be undone!', "xFerToSP", item.bi_case_id)}
                                  type="button"
                                >
                                  <i className="fa-prescription fas"></i> Transferred
                                </button>
                              );
                            } else if (step === "Treatment Scheduled") {
                              return (
                                <button key={index} className="btn btn-green btn-sm m-1 rounded-0 background-color-3"
                                  onClick={() => setDataForRequestModalTreatment({ isModalVisible: true, caseId: item.bi_case_id, prescriptionId: item.prescription_id, bentype: "sd" })}
                                  type="button"
                                >
                                  <i className="fa-times fas"></i> Treatment Scheduled
                                </button>
                              );
                            } else if (step.includes("Scheduled Tx Date")) {
                              return (
                                <button key={index} className="btn btn-link"
                                  onClick={() => setDataForRequestModalTreatment({ isModalVisible: true, caseId: item.bi_case_id, prescriptionId: item.prescription_id, bentype: "sd" })}
                                  type="button">
                                  {step}
                                </button>
                              );
                            } else {
                              return (<span key={index} className="text-black">{step}</span>);
                            }
                          })
                        }
                      </td>
                    )} */}
                    <td className="align-middle">
                      {item.date_of_treatment ? format(item.date_of_treatment, "MM/dd/yyyy") : 'N/A'}
                    </td>
                    <td className="align-middle">
                      {item.date_of_next_treatment ? format(item.date_of_next_treatment, "MM/dd/yyyy") : 'N/A'}
                    </td>


                    {!isManufacturer &&
                      <td>
                        <span className="btn btn-green rounded-0 text-nowrap" onClick={() => navigateToModalAndPage(item)}>
                          <i className="fas fa-sync-alt"></i> Re-run
                        </span>
                      </td>
                    }


                  </tr>
                ))
                : null}
            </tbody>
          </table>
        </div>

        {totalPages && totalPages > 1 ? (
          <div className="pagination pagination-sm pagination-lg justify-content-center flex-wrap mt-3">
            {currentPage !== 1 ? (
              <li className="page-item">
                <button className="btn btn-link page-link rounded-0" onClick={() => checkForPageChange({ selected: 0 })}>
                  <i className="bi bi-chevron-double-left"></i> First
                </button>
              </li>
            ) : null}

            <ReactPaginate
              nextLabel={<>Next <i className="bi bi-chevron-right"></i></>}
              onPageChange={checkForPageChange}
              pageRangeDisplayed={3}
              marginPagesDisplayed={2}
              pageCount={totalPages}
              previousLabel={<><i className="bi bi-chevron-left"></i> Previous</>}
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link rounded-0"
              nextClassName="page-item"
              nextLinkClassName="page-link rounded-0"
              breakLabel="..."
              breakClassName="page-item"
              breakLinkClassName="page-link"
              containerClassName="pagination pagination-sm pagination-lg"
              activeClassName="active"
              renderOnZeroPageCount={null}
              forcePage={currentPage - 1}
            />

            {currentPage !== totalPages ? (
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="btn btn-link page-link rounded-0" onClick={() => checkForPageChange({ selected: totalPages - 1 })}>
                  Last <i className="bi bi-chevron-double-right"></i>
                </button>
              </li>
            ) : null}
            <li className="page-item">
              <input
                type="number"
                min={1}
                max={totalPages}
                className="form-control page-link"
                style={{ width: "67px" }}
                placeholder="Page"
                value={pageSearch}
                onChange={(e) => setPageSearch(e.target.value)}
                onBlur={handlePageSearch}          // 👈 Fire on focus out
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur();        // 👈 Trigger blur = auto jump
                  }
                }}
              />
            </li>

          </div>
        ) : null}


        <AddNote
          isModalVisible={saveNoteModal.isModalVisible}
          caseId={saveNoteModal.caseId}
          refreshListing={() => redirectDecision()}
          hideModal={() => {
            setDataforNoteModal({
              isModalVisible: false,
              caseId: 0,
            });
          }}
        />
        <ReRunPatientModal
          isModalVisible={showReRunPatientModal.isModalVisible}
          caseId={showReRunPatientModal.caseId}
          prescriptionId={showReRunPatientModal.prescriptionId}
          bentype={showReRunPatientModal.bentype}
          title={showReRunPatientModal.title}
          investigated_at={showReRunPatientModal.investigated_at}
          hideModal={() => {
            setShowReRunPatientModal({
              isModalVisible: false,
              caseId: 0,
              prescriptionId: 0,
              bentype: '',
              title: "",
              investigated_at: undefined
            });
          }}
          refreshListing={() => redirectDecision()}
        /> 
        {showRequestTreatmentModal.isModalVisible && (
        <RequestDateOfTreatment
          isModalVisible={showRequestTreatmentModal.isModalVisible}
          caseId={showRequestTreatmentModal.caseId}
          prescriptionId={showRequestTreatmentModal.prescriptionId}
          bentype={showRequestTreatmentModal.bentype}
          investigated_at={showRequestTreatmentModal.investigated_at}
          hideModal={() => {
            setDataForRequestModalTreatment({
              isModalVisible: false,
              caseId: 0,
              prescriptionId: 0,
              bentype: '',
              investigated_at: ""
            });
          }}
          refreshListing={() => redirectDecision()}
        />
      )}

      <CancelTreatment
        isModalVisible={cancelTreatmentModal.isModalVisible}
        caseId={cancelTreatmentModal.caseId}
        prescriptionId={cancelTreatmentModal.prescriptionId}
        hideModal={() => {
          setDataForCancelModal({
            isModalVisible: false,
            caseId: 0,
            prescriptionId: 0
          });
        }}
        refreshListing={() => redirectDecision()}
      />
      </div>
    </div>
  );
};

export default OnTherapyPatient;
